import React, { useEffect, useState } from 'react';
import { ContentService } from '../services/content';
type TableReport = {
  status: string;
  rows: number;
  hint?: string;
};
type Report = Record<string, TableReport>;
/**
 * Dev diagnostic banner. Runs the Supabase connection check on mount and, if
 * ANY table is failing or empty, shows a compact dismissible banner explaining
 * exactly what's wrong per-table. When every table is connected it renders
 * nothing, so it stays out of the way once the data layer is healthy.
 */
const SupabaseStatus: React.FC = () => {
  const [report, setReport] = useState<Report | null>(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    ContentService.checkConnection().then(setReport);
  }, []);
  if (!report || dismissed) return null;
  const problems = Object.entries(report).filter(([, r]) => r.status !== '✅ connected');
  // Everything healthy → nothing to show.
  if (problems.length === 0) return null;
  const hasErrors = problems.some(([, r]) => r.status.includes('error'));
  return <div className="fixed bottom-6 left-6 z-[999] max-w-sm w-[calc(100%-3rem)] md:w-full animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-87">
      <div className="bg-zinc-900/95 backdrop-blur-xl border border-white/10 rounded-sm shadow-2xl overflow-hidden" data-id="element-88">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10" data-id="element-89">
          <div className="flex items-center gap-3" data-id="element-90">
            <span className={`w-2 h-2 rounded-full ${hasErrors ? 'bg-red-500' : 'bg-amber-400'} animate-pulse`} data-id="element-91" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-black text-white" data-id="element-92">
              supabase not connected
            </span>
          </div>
          <button onClick={() => setDismissed(true)} className="text-zinc-500 hover:text-white transition-colors text-[10px] uppercase tracking-widest font-bold cursor-target" aria-label="Dismiss diagnostic" data-id="element-93">
            dismiss
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 max-h-[40vh] overflow-y-auto" data-id="element-94">
          <p className="text-zinc-400 text-xs leading-relaxed lowercase" data-id="element-95">
            showing fallback (mock) content because these tables failed. fix
            them in your supabase dashboard, then reload.
          </p>
          <ul className="space-y-2.5" data-id="element-96">
            {problems.map(([table, r]) => <li key={table} className="text-xs leading-snug" data-id="element-97">
                <div className="flex items-center gap-2" data-id="element-98">
                  <span className={r.status.includes('error') ? 'text-red-400' : 'text-amber-400'} data-id="element-99">
                    {r.status.includes('error') ? '✕' : '○'}
                  </span>
                  <code className="font-mono text-white text-[11px]" data-id="element-100">
                    {table}
                  </code>
                  <span className="text-zinc-600 text-[10px] uppercase tracking-widest" data-id="element-101">
                    {r.status.includes('error') ? 'error' : 'empty'}
                  </span>
                </div>
                {r.hint && <p className="mt-1 ml-6 text-zinc-500 text-[11px] leading-snug" data-id="element-102">
                    {r.hint}
                  </p>}
              </li>)}
          </ul>
        </div>
      </div>
    </div>;
};
export default SupabaseStatus;